import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import {
  clientUploadsApi,
  type AdminClientDocumentRow,
  type ClientUploadStatus,
} from '../services/clientUploadsApi';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Filter,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { sanitizeRichTextHtml } from '@digitaltattoo/shared-utils';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useToast } from '../components/Toast';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { DateInput } from '../components/DateInput';

function formatBytes(bytes?: number | null) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(value?: string | Date | null) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR');
}

function statusBadge(status: ClientUploadStatus) {
  if (status === 'PENDING_REVIEW') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  if (status === 'APPROVED') return 'bg-green-100 text-green-800 border-green-200';
  return 'bg-red-100 text-red-800 border-red-200';
}

function sourceBadge(source: 'uploaded' | 'generated') {
  return source === 'generated'
    ? 'bg-blue-50 text-blue-700 border-blue-200'
    : 'bg-purple-50 text-purple-700 border-purple-200';
}

function sourceLabel(source: 'uploaded' | 'generated') {
  return source === 'generated' ? 'Généré' : 'Upload';
}

function statusLabel(status: ClientUploadStatus) {
  if (status === 'PENDING_REVIEW') return 'En attente';
  if (status === 'APPROVED') return 'Approuvé';
  return 'Refusé';
}

function documentKindLabel(row: AdminClientDocumentRow) {
  if (row.source === 'uploaded') {
    if (row.documentType === 'IDENTITY_CARD') return 'Pièce identité';
    if (row.documentType === 'MEDICAL_DOCUMENT') return 'Document médical';
    return 'Document client';
  }

  if (row.kind === 'CONTRACT') return 'Contrat';
  if (row.kind === 'INVOICE') return 'Document officiel';
  if (row.kind === 'APPOINTMENT') return 'Document rendez-vous';
  return row.kind || row.entityType || 'Document généré';
}

function signatureStatusBadge(row: AdminClientDocumentRow) {
  const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border';

  if (row.signatureStatus === 'NOT_APPLICABLE') {
    return <span className={`${base} bg-gray-100 text-gray-600 border-gray-200`}>Sans signature</span>;
  }
  if (row.signatureStatus === 'NOT_REQUIRED') {
    return <span className={`${base} bg-blue-50 text-blue-700 border-blue-200`}>Non requise</span>;
  }
  if (row.signatureStatus === 'SIGNED') {
    return <span className={`${base} bg-green-100 text-green-800 border-green-200`}>Signé</span>;
  }
  if (row.signatureStatus === 'PARTIAL') {
    return (
      <span className={`${base} bg-amber-100 text-amber-800 border-amber-200`}>
        Partiel ({row.signedRoles.length}/{row.requiredSigners.length})
      </span>
    );
  }
  return <span className={`${base} bg-red-100 text-red-700 border-red-200`}>En attente</span>;
}

function signatureSummary(row: AdminClientDocumentRow) {
  if (!row.requiresSignature) return 'Aucune signature requise';
  if (row.signatureStatus === 'SIGNED') {
    return `Roles signés : ${row.signedRoles.join(', ') || 'Tous'}`;
  }
  if (row.signatureStatus === 'PARTIAL') {
    const missing = row.requiredSigners.filter((role) => !row.signedRoles.includes(role));
    return `Signé : ${row.signedRoles.join(', ')} • Manque : ${missing.join(', ')}`;
  }
  return `Attendu : ${row.requiredSigners.join(', ') || '—'}`;
}

const CLIENT_UPLOADS_PAGE_SIZE = 50;

function stripHtmlToText(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').trim();
  } catch {
    return String(html || '').replace(/<[^>]*>/g, '').trim();
  }
}

export function ClientUploadsPage() {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { showToast, ToastComponent } = useToast();
  const [rows, setRows] = useState<AdminClientDocumentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [source, setSource] = useState<'uploaded' | 'generated' | ''>('');
  /** Filters generated documents only (API ignores when source = uploaded). */
  const [entityType, setEntityType] = useState<'APPOINTMENT' | 'CONTRACT' | 'INVOICE' | ''>('');
  const [status, setStatus] = useState<ClientUploadStatus | ''>('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const canReview = useMemo(() => !!user, [user]);
  const visibleIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const {
    selectedIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewRow, setReviewRow] = useState<AdminClientDocumentRow | null>(null);
  const [reviewStatus, setReviewStatus] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [reviewCommentHtml, setReviewCommentHtml] = useState<string>('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetRow, setResetRow] = useState<AdminClientDocumentRow | null>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetCommentHtml, setResetCommentHtml] = useState('');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<AdminClientDocumentRow | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'APPROVE' | 'REJECT' | 'RESET' | 'DELETE' | null>(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkCommentHtml, setBulkCommentHtml] = useState('');
  const [bulkHardDeleteConfirmOpen, setBulkHardDeleteConfirmOpen] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRow, setPreviewRow] = useState<AdminClientDocumentRow | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewMimeType, setPreviewMimeType] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const isBusy =
    loading || reviewSubmitting || resetSubmitting || deleteSubmitting || bulkSubmitting;

  const totalPages = Math.max(1, Math.ceil(total / CLIENT_UPLOADS_PAGE_SIZE));

  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);

  const load = async (pageOverride?: number) => {
    let nextPage = pageOverride ?? page;
    try {
      setLoading(true);
      setError(null);
      const listOnce = async (p: number) =>
        clientUploadsApi.list({
          page: p,
          limit: CLIENT_UPLOADS_PAGE_SIZE,
          source: source || undefined,
          entityType: entityType || undefined,
          status: status || undefined,
          search: search || undefined,
          from: from || undefined,
          to: to || undefined,
        });

      let data = await listOnce(nextPage);
      const totalCount = typeof data.total === 'number' ? data.total : 0;
      const maxPage = Math.max(1, Math.ceil(totalCount / CLIENT_UPLOADS_PAGE_SIZE));
      if (nextPage > maxPage) {
        nextPage = maxPage;
        data = await listOnce(nextPage);
      }

      setRows(data.data || []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      setPage(nextPage);
    } catch (e: any) {
      setError(e?.message || 'Failed to load uploads');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewMimeType(null);
    setPreviewRow(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setPreviewOpen(false);
  };

  const openDocumentViewer = async (row: AdminClientDocumentRow) => {
    if (!row.fileExists || !row.fileUrl) return;

    if (row.source === 'generated') {
      navigate(`/documents/view/${row.id}`);
      return;
    }

    setPreviewRow(row);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }

    try {
      const { blob, mimeType } = await clientUploadsApi.fetchFileBlob(row.id);
      const blobUrl = URL.createObjectURL(blob);
      setPreviewBlobUrl(blobUrl);
      setPreviewMimeType(mimeType || blob.type || null);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Impossible de charger le document';
      setPreviewError(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadBlob = async (row: AdminClientDocumentRow) => {
    if (!row.fileUrl) return;
    const { blob } = await clientUploadsApi.fetchBlobByUrl(row.fileUrl);
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    const name =
      row.source === 'uploaded'
        ? row.originalFileName || row.displayName || 'document'
        : `${row.templateCode || 'document'}_${row.entityType || 'generated'}`;
    a.download = String(name).replace(/[^\w\s.-]+/g, '_');
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  };

  const openReviewModal = (row: AdminClientDocumentRow, nextStatus: 'APPROVED' | 'REJECTED') => {
    if (row.source !== 'uploaded') return;
    setReviewRow(row);
    setReviewStatus(nextStatus);
    setReviewCommentHtml('');
    setReviewModalOpen(true);
  };

  const openResetModal = (row: AdminClientDocumentRow) => {
    if (row.source !== 'uploaded') return;
    setResetRow(row);
    setResetCommentHtml('');
    setResetModalOpen(true);
  };

  const rowTitle = (row: AdminClientDocumentRow) =>
    row.source === 'uploaded' ? row.displayName || row.originalFileName : row.templateCode;

  const openDeleteModal = (row: AdminClientDocumentRow) => {
    setDeleteRow(row);
    setDeleteModalOpen(true);
  };

  const openBulkModal = (action: 'APPROVE' | 'REJECT' | 'RESET' | 'DELETE') => {
    if (selectedCount === 0) return;
    if (action === 'DELETE') {
      setBulkHardDeleteConfirmOpen(true);
      return;
    }
    setBulkAction(action);
    setBulkCommentHtml('');
    setBulkModalOpen(true);
  };

  const confirmBulkAction = async (actionOverride?: 'APPROVE' | 'REJECT' | 'RESET' | 'DELETE') => {
    const action = actionOverride || bulkAction;
    if (!action || selectedCount === 0) return;
    const selectedRows = rows.filter((r) => selectedIds.has(r.id));
    const uploadedIds = selectedRows.filter((r) => r.source === 'uploaded').map((r) => r.id);
    const generatedIds = selectedRows.filter((r) => r.source === 'generated').map((r) => r.id);
    try {
      setBulkSubmitting(true);

      if (action === 'DELETE') {
        let generatedDeleted = new Set<string>();
        let blockedCount = 0;

        if (uploadedIds.length) {
          await Promise.all(uploadedIds.map((id) => clientUploadsApi.delete(id)));
        }

        if (generatedIds.length) {
          const resp = await clientUploadsApi.deleteGenerated(generatedIds);
          const deleted: string[] = Array.isArray(resp?.data?.deleted) ? resp.data.deleted : [];
          const blocked: Array<{ id: string; reason: string }> = Array.isArray(resp?.data?.blocked) ? resp.data.blocked : [];
          generatedDeleted = new Set(deleted);
          blockedCount = blocked.length;
          if (blocked.length) {
            setError(resp?.message || `Certaines suppressions ont été bloquées (${blocked.length}).`);
            showToast(resp?.message || `Certaines suppressions ont été bloquées (${blocked.length}).`, 'error', 7000);
          }
        }

        // Remove only what was actually deleted
        setRows((prev) =>
          prev.filter((r) => {
            if (!selectedIds.has(r.id)) return true;
            if (r.source === 'uploaded') return false;
            return !generatedDeleted.has(r.id);
          }),
        );

        const deletedCount = uploadedIds.length + generatedDeleted.size;
        if (blockedCount === 0) {
          showToast(
            `${deletedCount} document${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''} définitivement`,
            'success',
          );
        } else if (deletedCount > 0) {
          showToast(
            `${deletedCount} document${deletedCount !== 1 ? 's' : ''} supprimé${deletedCount !== 1 ? 's' : ''}. ${blockedCount} échec${blockedCount !== 1 ? 's' : ''}.`,
            'warning',
            7000,
          );
        }
      } else {
        if (!uploadedIds.length) {
          setError('Sélection invalide : seules les pièces uploadées peuvent être approuvées / refusées / remises en attente.');
          return;
        }
        const commentText = stripHtmlToText(bulkCommentHtml);
        const reviewComment = commentText ? bulkCommentHtml : undefined;
        const nextStatus =
          action === 'APPROVE' ? ('APPROVED' as const) : action === 'REJECT' ? ('REJECTED' as const) : ('PENDING_REVIEW' as const);

        await Promise.all(
          uploadedIds.map((id) =>
            clientUploadsApi.review(id, {
              status: nextStatus,
              ...(reviewComment ? { reviewComment } : {}),
            }),
          ),
        );

        // Optimistic update
        setRows((prev) =>
          prev.map((r) =>
            r.source === 'uploaded' && selectedIds.has(r.id)
              ? {
                  ...r,
                  status: nextStatus,
                  reviewedAt: nextStatus === 'PENDING_REVIEW' ? null : new Date().toISOString(),
                  reviewedByUserId: undefined,
                  reviewComment: reviewComment || null,
                }
              : r,
          ),
        );
      }

      setBulkModalOpen(false);
      setBulkAction(null);
      setBulkCommentHtml('');
      setBulkHardDeleteConfirmOpen(false);
      clearSelection();
      await load();
    } finally {
      setBulkSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRow) return;
    try {
      setDeleteSubmitting(true);
      if (deleteRow.source === 'uploaded') {
        await clientUploadsApi.delete(deleteRow.id);
      } else {
        const resp = await clientUploadsApi.deleteGenerated([deleteRow.id]);
        if (resp?.data?.blocked?.length) {
          const message = resp?.message || 'Suppression bloquée';
          setError(message);
          showToast(message, 'error', 7000);
          return;
        }
      }
      // Optimistic removal
      setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));
      setDeleteModalOpen(false);
      setDeleteRow(null);
      await load();
      showToast('Document supprimé définitivement', 'success');
    } catch (e: any) {
      const message = e?.message || 'Impossible de supprimer le document';
      setError(message);
      showToast(message, 'error', 7000);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const confirmResetToPending = async () => {
    if (!resetRow) return;
    if (resetRow.source !== 'uploaded') return;
    try {
      setResetSubmitting(true);
      await clientUploadsApi.review(resetRow.id, {
        status: 'PENDING_REVIEW',
        reviewComment: resetCommentHtml.trim() ? resetCommentHtml : undefined,
      });

      // Optimistic update so the list reflects the change immediately
      setRows((prev) =>
        prev
          .map((r) =>
            r.id !== resetRow.id
              ? r
              : {
                  ...r,
                  status: 'PENDING_REVIEW' as ClientUploadStatus,
                  reviewedAt: null,
                  reviewedByUserId: null,
                  reviewComment: resetCommentHtml.trim() ? resetCommentHtml : null,
                },
          )
          .filter((r) => (status ? (r.source === 'uploaded' ? r.status === status : true) : true)),
      );

      setResetModalOpen(false);
      setResetRow(null);
      setResetCommentHtml('');
      await load();
    } finally {
      setResetSubmitting(false);
    }
  };

  const resetFilters = async () => {
    setSource('');
    setEntityType('');
    setStatus('');
    setSearch('');
    setFrom('');
    setTo('');
    setPage(1);
    clearSelection();
    try {
      setLoading(true);
      setError(null);
      const data = await clientUploadsApi.list({ page: 1, limit: CLIENT_UPLOADS_PAGE_SIZE });
      setRows(data.data || []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      setPage(1);
    } catch (e: any) {
      setError(e?.message || 'Failed to load uploads');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async () => {
    if (!reviewRow) return;
    if (reviewRow.source !== 'uploaded') return;
    try {
      setReviewSubmitting(true);
      const commentText = stripHtmlToText(reviewCommentHtml);
      await clientUploadsApi.review(reviewRow.id, {
        status: reviewStatus,
        reviewComment: commentText ? reviewCommentHtml : undefined,
      });

      // Optimistic update so the list reflects the change immediately
      setRows((prev) =>
        prev
          .map((r) =>
            r.id !== reviewRow.id || r.source !== 'uploaded'
              ? r
              : {
                  ...r,
                  status: reviewStatus as ClientUploadStatus,
                  reviewedAt: new Date().toISOString(),
                  reviewedByUserId: undefined,
                  reviewComment: commentText ? reviewCommentHtml : null,
                },
          )
          .filter((r) => (status ? (r.source === 'uploaded' ? r.status === status : true) : true)),
      );

      setReviewModalOpen(false);
      setReviewRow(null);
      setReviewCommentHtml('');
      await load();
    } finally {
      setReviewSubmitting(false);
    }
  };

  return (
    <div className={`p-6 ${isBusy ? 'cursor-wait' : ''}`}>
      <ToastComponent />

      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">Documents clients</h1>
            <p className="text-textSecondary mt-1">
              {total} document{total !== 1 ? 's' : ''} au total
              {total > 0 && (
                <span className="text-textSecondary/80">
                  {' '}
                  · page {page} / {totalPages}
                  {rows.length > 0 && (
                    <>
                      {' '}
                      (affichage {(page - 1) * CLIENT_UPLOADS_PAGE_SIZE + 1}–
                      {(page - 1) * CLIENT_UPLOADS_PAGE_SIZE + rows.length})
                    </>
                  )}
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => void load(page)}
              className="btn-secondary flex items-center space-x-2"
              disabled={isBusy}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Rafraîchir</span>
            </button>

            {canReview && selectedCount > 0 && (
              <>
                <button
                  onClick={() => openBulkModal('APPROVE')}
                  disabled={isBusy}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 inline-flex items-center space-x-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Approuver ({selectedCount})</span>
                </button>
                <button
                  onClick={() => openBulkModal('REJECT')}
                  disabled={isBusy}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Refuser ({selectedCount})</span>
                </button>
                <button
                  onClick={() => openBulkModal('RESET')}
                  disabled={isBusy}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 inline-flex items-center space-x-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Remettre en attente ({selectedCount})</span>
                </button>
                <button
                  onClick={() => openBulkModal('DELETE')}
                  disabled={isBusy}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                  title="Supprimer définitivement les documents sélectionnés"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Suppression définitive ({selectedCount})</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="relative md:col-span-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un client..."
              className="input-field pl-10"
            />
          </div>

          <div className="relative md:col-span-2">
            <select
              value={source}
              onChange={(e) => setSource(e.target.value as 'uploaded' | 'generated' | '')}
              className="input-field appearance-none pr-10"
            >
              <option value="">Toutes les sources</option>
              <option value="uploaded">Uploads</option>
              <option value="generated">Générés</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative md:col-span-2">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as ClientUploadStatus | '')}
              className="input-field appearance-none pr-10"
            >
              <option value="">Tous les statuts</option>
              <option value="PENDING_REVIEW">En attente</option>
              <option value="APPROVED">Approuvé</option>
              <option value="REJECTED">Refusé</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Date de début</label>
            <DateInput
              value={from}
              onChange={(value) => setFrom(value)}
              className="input-field"
            />
          </div>

          <div className="relative md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Date de fin</label>
            <DateInput
              value={to}
              onChange={(value) => setTo(value)}
              className="input-field"
            />
          </div>

          <button
            onClick={() => void resetFilters()}
            className="btn-secondary flex items-center justify-center space-x-2 md:col-span-1"
            disabled={isBusy}
          >
            <Filter className="w-4 h-4" />
            <span className="md:hidden">Effacer</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end mt-4">
          <div className="relative md:col-span-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Type d’entité (documents générés)
            </label>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value as typeof entityType)}
              disabled={source === 'uploaded'}
              className="input-field appearance-none pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                source === 'uploaded'
                  ? 'Filtre non applicable aux seuls uploads client'
                  : 'Filtre les lignes issues de generated_documents (RDV, contrat, facture)'
              }
            >
              <option value="">Tous types</option>
              <option value="APPOINTMENT">Rendez-vous</option>
              <option value="CONTRACT">Contrat (fiche contrat)</option>
              <option value="INVOICE">Facture</option>
            </select>
            <ChevronDown className="absolute right-3 top-9 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => void load(1)}
            className="btn-primary flex items-center space-x-2"
            disabled={isBusy}
          >
            <span>Filtrer</span>
          </button>
          {selectedCount > 0 && (
            <button
              onClick={clearSelection}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
              disabled={isBusy}
            >
              Vider la sélection
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={() => void load(page)}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            Réessayer
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  {canReview && (
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      disabled={rows.length === 0 || isBusy}
                      onChange={toggleSelectAllVisible}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      aria-label="Sélectionner tous les documents de la page"
                    />
                  )}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Créé le</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signature</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fichier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row) => (
                <tr key={row.id} className="group hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {canReview && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        disabled={isBusy}
                        onChange={() => toggleSelect(row.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        aria-label={`Sélectionner le document ${rowTitle(row)}`}
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <div>{formatDate(row.createdAt)}</div>
                    {row.source === 'uploaded' && (
                      <div className="text-xs text-gray-500">Revue: {formatDate(row.reviewedAt || null)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {row.client?.firstName || ''} {row.client?.lastName || ''}
                    </div>
                    <div className="text-sm text-gray-500">{row.client?.email || '—'}</div>
                    {row.client?.phone && <div className="text-xs text-gray-500">{row.client.phone}</div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${sourceBadge(row.source)}`}>
                      {sourceLabel(row.source)}
                    </span>
                  </td>
                  <td className="px-6 py-4 min-w-[260px]">
                    <div className="text-sm font-medium text-gray-900">
                      {rowTitle(row) || 'Document sans nom'}
                    </div>
                    {row.source === 'generated' && (
                      <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                        {row.entityType === 'APPOINTMENT' && (
                          <div>
                            Lié au rendez-vous{' '}
                            <Link
                              to={`/appointments/${row.entityId}`}
                              className="text-primary-600 hover:text-primary-700 hover:underline font-mono"
                            >
                              {row.entityId}
                            </Link>
                          </div>
                        )}
                        {row.entityType === 'CONTRACT' && (
                          <div className="text-gray-600">
                            <span className="font-mono break-all">{row.entityId}</span>
                            <span className="text-[11px] text-gray-400 block mt-0.5">
                              → enregistrement <code className="text-[11px]">contracts</code> ; cette ligne = fichier
                              généré (template lié)
                            </span>
                          </div>
                        )}
                        {row.entityType === 'INVOICE' && (
                          <div className="font-mono break-all">Facture : {row.entityId}</div>
                        )}
                        {row.entityType &&
                          row.entityType !== 'APPOINTMENT' &&
                          row.entityType !== 'CONTRACT' &&
                          row.entityType !== 'INVOICE' && (
                            <div className="font-mono break-all">
                              {row.entityType} : {row.entityId}
                            </div>
                          )}
                      </div>
                    )}
                    {row.source === 'uploaded' && row.descriptionHtml && (
                      <div
                        className="text-xs text-gray-500 mt-1 line-clamp-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_p]:my-0.5"
                        dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(row.descriptionHtml) }}
                      />
                    )}
                    {row.source === 'uploaded' && row.reviewComment && (
                      <div
                        className="text-xs text-amber-700 mt-1 line-clamp-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_p]:my-0.5"
                        dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(row.reviewComment) }}
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <div>
                      <div className="font-medium text-gray-900">{documentKindLabel(row)}</div>
                      <div className="text-xs text-gray-500">
                        {row.source === 'uploaded' ? row.documentType : row.templateCode}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 min-w-[220px]">
                    <div className="space-y-1">
                      <div className="text-sm text-gray-900">
                        {row.requiresSignature ? 'Signature requise' : 'Pas de signature requise'}
                      </div>
                      <div className="text-xs text-gray-500">{signatureSummary(row)}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 min-w-[220px]">
                    <div className="space-y-1">
                      <div>{signatureStatusBadge(row)}</div>
                      {row.source === 'generated' && (
                        <div className="text-xs text-gray-500">
                          {row.requiredSigners.length > 0
                            ? `${row.signedRoles.length}/${row.requiredSigners.length} signataire${row.requiredSigners.length > 1 ? 's' : ''}`
                            : 'Aucun signataire attendu'}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                        row.fileExists
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-red-50 text-red-700 border-red-200'
                      }`}
                    >
                      {row.fileExists ? 'Présent' : 'Absent'}
                    </span>
                    <div className="text-xs text-gray-500 mt-1">
                      {row.source === 'uploaded'
                        ? formatBytes(row.sizeBytes)
                        : row.fileUrl
                          ? 'Téléchargeable'
                          : 'Aucun lien'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {row.source === 'uploaded' ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusBadge(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">Généré</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => void openDocumentViewer(row)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded"
                        title="Voir le document"
                        disabled={isBusy || !row.fileExists || !row.fileUrl}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => void downloadBlob(row)}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded"
                        title="Télécharger le document"
                        disabled={isBusy || !row.fileExists || !row.fileUrl}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {canReview && row.source === 'uploaded' && row.status === 'PENDING_REVIEW' && (
                        <>
                          <button
                            onClick={() => openReviewModal(row, 'APPROVED')}
                            className="text-green-600 hover:text-green-700 p-1 rounded"
                            title="Approuver"
                            disabled={isBusy}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openReviewModal(row, 'REJECTED')}
                            className="text-red-600 hover:text-red-700 p-1 rounded"
                            title="Refuser"
                            disabled={isBusy}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {canReview && row.source === 'uploaded' && row.status !== 'PENDING_REVIEW' && (
                        <button
                          onClick={() => openResetModal(row)}
                          className="text-yellow-600 hover:text-yellow-700 p-1 rounded"
                          title="Remettre en attente"
                          disabled={isBusy}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                      {canReview && (
                        <button
                          onClick={() => openDeleteModal(row)}
                          className="text-red-600 hover:text-red-700 p-1 rounded"
                          title="Supprimer définitivement le document"
                          disabled={isBusy}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {total > 0 && (
          <div className="dt-list-toolbar flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-6 py-3 bg-gray-50">
            <p className="text-sm text-gray-600">
              {total} résultat{total !== 1 ? 's' : ''} · page {page} sur {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void load(page - 1)}
                disabled={isBusy || page <= 1}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Précédent
              </button>
              <button
                type="button"
                onClick={() => void load(page + 1)}
                disabled={isBusy || page >= totalPages}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="text-center py-12">
            <Trash2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun document trouvé</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search || status || source || entityType || from || to
                ? 'Essayez de modifier vos filtres de recherche.'
                : 'Aucun document client disponible pour le moment.'}
            </p>
          </div>
        )}
      </div>

      {loading && rows.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 cursor-wait">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Chargement...</span>
          </div>
        </div>
      )}

      {/* Review modal */}
      {reviewModalOpen && reviewRow && reviewRow.source === 'uploaded' && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => (reviewSubmitting ? null : setReviewModalOpen(false))}
            />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {reviewRow.displayName || reviewRow.originalFileName}
                    </h3>
                    <div className="mt-1 text-sm text-gray-500 space-y-0.5">
                      <div>
                        Date : {formatDate(reviewRow.createdAt)} • Statut actuel : {reviewRow.status}
                      </div>
                      <div>
                        Client : {reviewRow.client?.firstName || ''} {reviewRow.client?.lastName || ''}{' '}
                        {reviewRow.client?.email ? `(${reviewRow.client.email})` : ''}
                        {reviewRow.client?.phone ? ` • ${reviewRow.client.phone}` : ''}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => (reviewSubmitting ? null : setReviewModalOpen(false))}
                    className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    disabled={reviewSubmitting}
                    title="Fermer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Commentaire (optionnel)</label>
                  <RichTextEditor
                    value={reviewCommentHtml}
                    onChange={setReviewCommentHtml}
                    disabled={reviewSubmitting}
                    placeholder="Ajoutez un commentaire (optionnel)…"
                  />
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={() => void submitReview()}
                  disabled={reviewSubmitting}
                  className={`w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto sm:text-sm ${
                    reviewStatus === 'APPROVED'
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {reviewSubmitting ? 'En cours…' : reviewStatus === 'APPROVED' ? 'Approuver' : 'Refuser'}
                </button>
                <button
                  type="button"
                  onClick={() => setReviewModalOpen(false)}
                  disabled={reviewSubmitting}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={resetModalOpen}
        title={
          resetRow && resetRow.source === 'uploaded'
            ? resetRow.displayName || resetRow.originalFileName
            : 'Remettre en attente'
        }
        message=""
        confirmLabel="Remettre en attente"
        cancelLabel="Annuler"
        confirmVariant="warning"
        isLoading={resetSubmitting}
        onConfirm={() => void confirmResetToPending()}
        onClose={() => {
          if (resetSubmitting) return;
          setResetModalOpen(false);
          setResetRow(null);
          setResetCommentHtml('');
        }}
      >
        <div className="space-y-2">
          {resetRow && resetRow.source === 'uploaded' && (
            <div className="text-sm text-gray-600 space-y-0.5">
              <div>
                Date : {formatDate(resetRow.createdAt)} • Statut actuel : {resetRow.status}
              </div>
              <div>
                Client : {resetRow.client?.firstName || ''} {resetRow.client?.lastName || ''}{' '}
                {resetRow.client?.email ? `(${resetRow.client.email})` : ''}
                {resetRow.client?.phone ? ` • ${resetRow.client.phone}` : ''}
              </div>
            </div>
          )}
          <label className="block text-sm font-medium text-gray-700">Note (optionnel)</label>
          <RichTextEditor
            value={resetCommentHtml}
            onChange={setResetCommentHtml}
            disabled={resetSubmitting}
            placeholder="Expliquez pourquoi vous ré-ouvrez ce document (optionnel)…"
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        isOpen={bulkModalOpen}
        title={
          bulkAction === 'APPROVE'
            ? `Approuver ${selectedCount} document(s)`
            : bulkAction === 'REJECT'
              ? `Refuser ${selectedCount} document(s)`
              : bulkAction === 'RESET'
                ? `Remettre en attente ${selectedCount} document(s)`
                : bulkAction === 'DELETE'
                  ? `Supprimer ${selectedCount} document(s)`
                  : 'Action'
        }
        message={
          bulkAction === 'DELETE'
            ? 'Cette action supprimera définitivement les documents sélectionnés (métadonnées + fichiers).'
            : 'Vous allez appliquer cette action à tous les documents sélectionnés.'
        }
        confirmLabel={
          bulkAction === 'APPROVE'
            ? 'Approuver'
            : bulkAction === 'REJECT'
              ? 'Refuser'
              : bulkAction === 'RESET'
                ? 'Remettre en attente'
                : bulkAction === 'DELETE'
                  ? 'Supprimer'
                  : 'Confirmer'
        }
        cancelLabel="Annuler"
        confirmVariant={bulkAction === 'DELETE' ? 'danger' : bulkAction === 'RESET' ? 'warning' : 'primary'}
        isLoading={bulkSubmitting}
        onConfirm={() => void confirmBulkAction()}
        onClose={() => {
          if (bulkSubmitting) return;
          setBulkModalOpen(false);
          setBulkAction(null);
          setBulkCommentHtml('');
        }}
      >
        {bulkAction && bulkAction !== 'DELETE' ? (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Commentaire (optionnel, appliqué à toute la sélection)</label>
            <RichTextEditor
              value={bulkCommentHtml}
              onChange={setBulkCommentHtml}
              disabled={bulkSubmitting}
              placeholder="Ajoutez un commentaire (optionnel)…"
            />
          </div>
        ) : null}
      </ConfirmModal>

      <ConfirmDefinitiveDeleteModal
        isOpen={deleteModalOpen}
        onConfirm={() => void confirmDelete()}
        onClose={() => {
          if (deleteSubmitting) return;
          setDeleteModalOpen(false);
          setDeleteRow(null);
        }}
        entityLabel="document"
        count={deleteRow ? 1 : 0}
        isLoading={deleteSubmitting}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirmOpen}
        onConfirm={() => {
          setBulkAction('DELETE');
          void confirmBulkAction('DELETE');
        }}
        onClose={() => {
          if (bulkSubmitting) return;
          setBulkHardDeleteConfirmOpen(false);
        }}
        entityLabel="document(s)"
        count={selectedCount}
        isLoading={bulkSubmitting}
      />

      <DocumentPreviewModal
        isOpen={previewOpen}
        title={previewRow ? String(rowTitle(previewRow)) : 'Document'}
        loading={previewLoading}
        error={previewError}
        blobUrl={previewBlobUrl}
        mimeType={previewMimeType}
        onClose={closePreview}
        onDownload={previewRow ? () => void downloadBlob(previewRow) : undefined}
      />
    </div>
  );
}


